import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import {
    ArrowLeft,
    AlertCircle,
    Image,
    AlertTriangle,
    PlusCircle,
    Utensils,
} from "lucide-react";

import InfoMeal from "../../../components/Info/InfoMeal";
import InfoDrink from "../../../components/Info/InfoDrink";
import SkeletonDettaglioProdotto from "../../../components/skeleton/SkeletonDettaglioProdotto";
import { getIconCategory } from "../../../utils/utils";

import productsApi from "../../../services/productsApi";

export default function DettaglioProdotto() {
    const navigate = useNavigate();
    const location = useLocation();

    const products = Array.isArray(location.state?.products) ? location.state.products : [];
    const initialIndex = typeof location.state?.currentIndex === "number" ? location.state.currentIndex : 0;

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [product, setProduct] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const loadProductDetails = async (id) => {
        try {
            setIsLoading(true);
            const response = await productsApi.retrieveProduct(id);
            if (response?.data?.data) {
                setProduct(response.data.data);
            }
        } catch (err) {
            console.error("Errore nel recuperare i dettagli del prodotto:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (products.length > 0 && products[currentIndex]?.id) {
            loadProductDetails(products[currentIndex].id);
        }
    }, [currentIndex]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (currentIndex < products.length - 1) setCurrentIndex((prev) => prev + 1);
        },
        onSwipedRight: () => {
            if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
        },
        preventScrollOnSwipe: true,
        trackMouse: true,
    });

    if (isLoading || !product) return <SkeletonDettaglioProdotto />;

    const CategoryIcon = getIconCategory(product.categoryIcon);

    return (
        <main className="min-h-safe-screen bg-[#0D111A] text-secondary pb-28 pt-[64px] relative" {...swipeHandlers}>
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-surface px-4 py-3 border-b border-accent shadow-md">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => {
                            if (window.history.length > 2) {
                                navigate(-1);
                            } else {
                                navigate("/menu");
                            }
                        }}
                        className="text-secondary p-2"
                        aria-label="Torna al menu"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div className="flex items-center gap-2 text-primary text-sm font-bold truncate">
                        {CategoryIcon && CategoryIcon}
                        <strong>{product.categoryName}</strong>
                    </div>
                    <div className="w-6" />
                </div>
            </header>

            {/* Immagine */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="px-6 pt-6"
            >
                <div
                    className="relative mx-auto w-72 h-72 rounded-full overflow-hidden border-4 border-primary bg-[#181818] shadow-[0_0_60px_rgba(233,69,69,0.4)]"
                >
                    <div className="absolute inset-0 w-full h-full object-cover">
                        {product.image ? (
                            <img
                                src={product.image}
                                alt={product.title}
                                crossOrigin="anonymous"
                                className="w-full h-full object-cover"
                                draggable={false}
                                loading='eager'
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted bg-background">
                                <Image className="w-6 h-6" />
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Titolo */}
            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="mt-5 text-center text-xl font-semibold tracking-tight text-secondary px-4 leading-snug break-words max-w-md mx-auto"
            >
                {product.title}
            </motion.h1>

            {/* Contenuto */}
            <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="px-6 max-w-md mx-auto space-y-6 pt-6"
            >
                {product.type === "drink" ? (
                    <InfoDrink
                        price={product.price}
                        categoryIcon={product.categoryIcon}
                        alcoholPercentage={product.alcoholPercentage}
                        quantity={product.quantity}
                        isAvailable={product.available}
                    />
                ) : (
                    <InfoMeal
                        calories={product.calories}
                        price={product.price}
                        categoryIcon={product.categoryIcon}
                        isAvailable={product.available}
                    />
                )}

                {product.description && (
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                        <h3 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> Descrizione
                        </h3>
                        <p className="text-sm text-muted">{product.description}</p>
                    </motion.div>
                )}

                {Array.isArray(product.ingredients) && product.ingredients.length > 0 && (
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
                        <h3 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
                            <Utensils className="w-4 h-4" /> Ingredienti
                        </h3>
                        <ul className="list-disc list-inside text-sm text-muted space-y-1">
                            {product.ingredients.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </motion.div>
                )}

                {product.type !== "drink" && (
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
                        <h3 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
                            <PlusCircle className="w-4 h-4" /> Extra
                        </h3>
                        <p className="text-sm text-muted">Ogni ingrediente extra comporterà un prezzo aggiuntivo.</p>
                    </motion.div>
                )}

                {product.allergens && (
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.3 }}>
                        <h3 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Allergeni
                        </h3>
                        <p className="text-sm text-muted">{product.allergens}</p>
                    </motion.div>
                )}
            </motion.section>

            {/* Navigazione */}
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[90%] bg-surface border border-accent rounded-2xl px-6 py-3 flex justify-between items-center text-sm z-30 shadow-xl">
                <button
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex((prev) => prev - 1)}
                    className="text-primary disabled:opacity-30 hover:underline transition"
                >
                    ← Precedente
                </button>

                <span className="text-muted font-medium">
                    {currentIndex + 1} / {products.length}
                </span>

                <button
                    disabled={currentIndex === products.length - 1}
                    onClick={() => setCurrentIndex((prev) => prev + 1)}
                    className="text-primary disabled:opacity-30 hover:underline transition"
                >
                    Successivo →
                </button>
            </div>
        </main>
    );
}
